import * as React from 'react';
import { OccupantByPermission, PropertyWithOccupants, UserPermissions } from '@centecomm/portal-service';
import { useTranslation } from 'react-i18next';
import { ArrowUpCircle, ArrowDownCircle } from '../../../Icons';
import { LoadingContent, SecondaryTitleWithAction, ToggleIcon } from '../../PageElements';
import { Search } from '../../Search';
import { StateProperty } from '../StateProperty';
import styles from './select-from-all-occupants.module.css';

interface SelectFromAllOccupantsProps {
    properties: PropertyWithOccupants[];
    selectedOccupants: number[];
    allOccupants: number[];
    propertiesAreLoaded: boolean;
    toggleOccupants: (occupantIds: number[], operator?: string) => void;
    searchHandler: (searchTerm: string) => void;
    permission?: UserPermissions;
    toggleListOpen?: boolean;
}

const SelectFromAllOccupants: React.FC<SelectFromAllOccupantsProps> = ({
    properties,
    selectedOccupants,
    allOccupants,
    propertiesAreLoaded,
    toggleOccupants,
    searchHandler,
    permission = UserPermissions.CreateNews,
    toggleListOpen = false,
}) => {
    const { t } = useTranslation(['forms']);
    const [showSelected, toggleShowSelected] = React.useState<boolean>(false);

    const selectedOccupantsSet = new Set(selectedOccupants);
    const allOccupantsSet = new Set(allOccupants);

    const [occupantsList, toggleList] = React.useState<boolean>(toggleListOpen);

    const action = {
        callBack: () => {
            toggleList(!occupantsList);
            searchHandler('');
        },
    };

    const search = (searchTerm: string) => {
        searchHandler(searchTerm);
    };

    const clearSearch = () => {
        // reset search related values to default
        searchHandler('');
    };

    return propertiesAreLoaded ? (
        <>
            <SecondaryTitleWithAction
                title={
                    selectedOccupants.length > 0
                        ? `${selectedOccupants.length} ${t('forms:selectors.tenants.genericTenantsSelected')}`
                        : t('forms:selectors.tenants.placeholderPlural')
                }
                action={action}
                ActionIcon={occupantsList ? ArrowUpCircle : ArrowDownCircle}
            />
            {occupantsList && (
                <>
                    <Search
                        handler={search}
                        placeholder={t('forms:selectors.tenants.searchByProperty')}
                        cleanUpCallback={clearSearch}
                        clearCallback={clearSearch}
                        debounceDelay={500}
                    />
                    <ToggleIcon
                        active={showSelected}
                        message={t('forms:selectors.showSelectedOnly')}
                        toggle={() => toggleShowSelected(!showSelected)}
                    />
                    <div className={styles.StatePropertyList}>
                        {properties.map(({ id, name, occupants }: PropertyWithOccupants) => (
                            <StateProperty
                                key={`state-property-${id}`}
                                id={id}
                                name={name}
                                occupants={occupants as OccupantByPermission[]}
                                occupantIds={occupants.map((o) => o.id)}
                                showSelectedOnly={showSelected}
                                toggleOccupants={toggleOccupants}
                                selectedOccupants={selectedOccupantsSet}
                                visibleOccupants={allOccupantsSet}
                                permission={permission}
                            />
                        ))}
                    </div>
                </>
            )}
        </>
    ) : (
        <LoadingContent />
    );
};

export default SelectFromAllOccupants;
